import React, { useMemo } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Pause, Play } from 'lucide-react';
import { VoiceScrubber } from './VoiceScrubber';
import { SpeedChip } from './SpeedChip';
import { buildWaveform, formatVoiceTime, useVoicePlayback } from '../../hooks/useVoicePlayback';
interface FullCallPlayerProps {
  /** Call length as shown in the UI, e.g. "2m 32s". */
  durationLabel: string;
  seed: string;
  isLight: boolean;
  isActive: boolean;
  onActivate: () => void;
  onDeactivate: () => void;
}

/** Full-call recording in the summary — same player as the transcript bubbles. */
export function FullCallPlayer({
  durationLabel,
  seed,
  isLight,
  isActive,
  onActivate,
  onDeactivate
}: FullCallPlayerProps) {
  const duration = useMemo(() => parseDuration(durationLabel), [durationLabel]);
  const bars = useMemo(() => buildWaveform(seed, 40), [seed]);
  const player = useVoicePlayback({
    duration,
    isActive,
    onActivate,
    onDeactivate
  });
  return <div className={`flex w-full items-center gap-3 rounded-xl border px-3.5 py-2.5 transition-colors ${player.showPlayer ? 'border-vexu/20 bg-vexu/10' : isLight ? 'border-slate-100 bg-slate-50' : 'border-white/[0.06] bg-white/[0.03]'}`} data-id="element-403" data-mp="2vd8a6sgs9d6t">
      <button type="button" onClick={player.togglePlay} aria-label={player.playing ? 'Pause call recording' : 'Play full call'} className={`grid h-8 w-8 flex-shrink-0 place-items-center rounded-full transition-colors ${player.showPlayer ? 'bg-vexu text-white' : isLight ? 'bg-white text-slate-500 shadow-sm border border-slate-100' : 'bg-white/10 text-zinc-400'}`} data-id="element-404" data-mp="358ia9anmfahy">
        {player.playing ? <Pause className="h-3.5 w-3.5 fill-current" data-id="element-405" data-mp="2qdvnc7pww648" /> : <Play className="ml-0.5 h-3.5 w-3.5 fill-current" data-id="element-406" data-mp="2qf6rw9r6lhwe" />}
      </button>

      <div className="min-w-0 flex-1" data-id="element-407" data-mp="1tv7kaxolxxrc">
        <AnimatePresence mode="wait" initial={false} data-id="element-408" data-mp="1xwjap5fb2n0l">
          {player.showPlayer ? <motion.div key="player" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.15
        }} className="flex items-center gap-2" data-id="element-409" data-mp="317p1734ps6ap">
              <VoiceScrubber bars={bars} progress={player.progress} duration={duration} elapsed={player.elapsed} setProgress={player.setProgress} draggingRef={player.draggingRef} fillClass="bg-vexu" trackClass={isLight ? 'bg-slate-300' : 'bg-white/20'} thumbClass="bg-vexu" data-id="element-410" data-mp="3azuucg6pctrh" />
              <SpeedChip speed={player.speed} onClick={player.cycleSpeed} surface={isLight ? 'light' : 'dark'} data-id="element-411" data-mp="2n7o1hr06r4wt" />
            </motion.div> : <motion.button key="label" type="button" onClick={player.togglePlay} initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.15
        }} className={`text-left text-[12px] font-medium ${isLight ? 'text-slate-700' : 'text-zinc-300'}`} data-id="element-412" data-mp="2iq9o0f0k8is">
              Play Full Call
            </motion.button>}
        </AnimatePresence>
      </div>

      <span className={`flex-shrink-0 text-[10px] font-medium tabular-nums ${player.showPlayer ? 'text-vexu' : isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-413" data-mp="sta5nym7pxtx">
        {player.showPlayer ? `${formatVoiceTime(player.elapsed)} / ${formatVoiceTime(duration)}` : durationLabel}
      </span>
    </div>;
}
function parseDuration(label: string) {
  const m = /(\d+)\s*m/.exec(label);
  const s = /(\d+)\s*s/.exec(label);
  const total = (m ? Number(m[1]) * 60 : 0) + (s ? Number(s[1]) : 0);
  if (total > 0) return total;
  const colon = /(\d+):(\d+)/.exec(label);
  return colon ? Number(colon[1]) * 60 + Number(colon[2]) : 30;
}